import React, { useContext, useEffect, useMemo, useState } from "react";
import { Alert, Button, Card, Modal, Spin, Table, Tabs, Tag, Tooltip, Typography } from "antd";
import {
  ArrowDownOutlined,
  ArrowRightOutlined,
  FullscreenExitOutlined,
  FullscreenOutlined,
  LineChartOutlined,
  RadarChartOutlined,
  ReloadOutlined,
  TableOutlined,
} from "@ant-design/icons";
import apiClient from "../../services/apiClient";
import LandingLayout from "./LandingLayout";
import "./RiskBasedPricing.css";
import { ThemeContext } from "../../context/ThemeContext";
import { useNavigate } from "react-router-dom";
import { LineChart } from "@mui/x-charts/LineChart";
import { alpha, useTheme } from "@mui/material/styles";
import { useDrawingArea, useXScale } from "@mui/x-charts/hooks";

const { Paragraph, Text } = Typography;

/** Daily Yields / Benchmark matrices: use full layout width; tall body for easier review */
const RBP_TABLE_SCROLL = { x: "max-content", y: "calc(100vh - 260px)" };

/** Per-year `isValid === false` flags for vertical benchmark marks in the modal chart. */
const BenchmarkGraphInvalidContext = React.createContext({
  verticalInvalid: /** @type {boolean[]} */ ([]),
});

/**
 * Renders default circle marks, or a circle with an X inside when that point is invalid (`isValid === false`).
 */
function BenchmarkLineChartMark(props) {
  const { id, dataIndex, x, y, color, onClick, isFaded, isHighlighted } = props;
  const { verticalInvalid } = React.useContext(BenchmarkGraphInvalidContext);
  const invalid = id === "vertical" ? Boolean(verticalInvalid[dataIndex]) : false;

  const theme = useTheme();
  const paper = (theme.vars || theme).palette.background.paper;

  if (!invalid) {
    return (
      <circle
        cx={x}
        cy={y}
        r={5}
        fill={paper}
        stroke={color}
        strokeWidth={isHighlighted ? 3 : 2}
        onClick={onClick}
        style={{ cursor: onClick ? "pointer" : "unset", opacity: isFaded ? 0.35 : 1 }}
      />
    );
  }

  const opacity = isFaded ? 0.35 : 1;
  const r = 6;
  const arm = 3.8;

  return (
    <g onClick={onClick} style={{ cursor: onClick ? "pointer" : "unset", opacity }} pointerEvents="all">
      <circle
        cx={x}
        cy={y}
        r={r}
        fill="#fecaca"
        stroke="#dc2626"
        strokeWidth={isHighlighted ? 2.5 : 1.6}
      />
      <line
        x1={x - arm}
        y1={y - arm}
        x2={x + arm}
        y2={y + arm}
        stroke="#dc2626"
        strokeWidth={1.35}
        strokeLinecap="round"
        pointerEvents="none"
      />
      <line
        x1={x - arm}
        y1={y + arm}
        x2={x + arm}
        y2={y - arm}
        stroke="#dc2626"
        strokeWidth={1.35}
        strokeLinecap="round"
        pointerEvents="none"
      />
    </g>
  );
}

function clamp(num, min, max) {
  return Math.min(max, Math.max(min, num));
}

function hexToRgb(hex) {
  const h = String(hex).replace("#", "").trim();
  if (h.length !== 6) return { r: 0, g: 0, b: 0 };
  return {
    r: parseInt(h.slice(0, 2), 16),
    g: parseInt(h.slice(2, 4), 16),
    b: parseInt(h.slice(4, 6), 16),
  };
}

function rgbToHex({ r, g, b }) {
  const to = (x) => clamp(Math.round(x), 0, 255).toString(16).padStart(2, "0");
  return `#${to(r)}${to(g)}${to(b)}`;
}

function lerp(a, b, t) {
  return a + (b - a) * t;
}

function lerpHex(a, b, t) {
  const c1 = hexToRgb(a);
  const c2 = hexToRgb(b);
  return rgbToHex({
    r: lerp(c1.r, c2.r, t),
    g: lerp(c1.g, c2.g, t),
    b: lerp(c1.b, c2.b, t),
  });
}

function yieldColor(y) {
  // Financial-ish palette: slate (low) -> teal (mid) -> green (high)
  // Typical ranges here are ~5-18.
  const v = clamp(Number(y), 4, 18);
  const t = (v - 4) / (18 - 4);
  if (t < 0.6) return lerpHex("#334155", "#0f766e", t / 0.6);
  return lerpHex("#0f766e", "#15803d", (t - 0.6) / 0.4);
}

function scanColor(score) {
  // SCAN is 0..100: red -> amber -> green
  const s = clamp(Number(score), 0, 100);
  if (s < 60) return lerpHex("#b91c1c", "#d97706", s / 60);
  return lerpHex("#d97706", "#15803d", (s - 60) / 40);
}

const DEFAULT_RATINGS_ORDER = [
  "G-Sec",
  "SDL",
  "AAA",
  "AA+",
  "AA",
  "AA-",
  "A+",
  "A",
  "A-",
  "BBB+",
  "BBB",
];

function buildYearToEntriesIndex(series) {
  const index = {};
  if (!Array.isArray(series)) return index;

  for (const item of series) {
    const year = Number(item?.year);
    const value = item?.yield;
    const isin = item?.isin;
    const residualMaturityMonths = item?.residualMaturityMonths;
    const scanScore = item?.scanScore;
    const tradeValue = item?.tradeValue;
    const noOfTrades = item?.noOfTrades;
    if (!Number.isFinite(year)) continue;
    if (value === undefined || value === null) continue;

    if (!index[year]) index[year] = [];
    index[year].push({
      yield: Number(value),
      isin: typeof isin === "string" ? isin : "",
      residualMaturityMonths:
        residualMaturityMonths === undefined || residualMaturityMonths === null
          ? null
          : Number(residualMaturityMonths),
      scanScore: scanScore === undefined || scanScore === null ? null : Number(scanScore),
      tradeValue: tradeValue === undefined || tradeValue === null ? null : Number(tradeValue),
      noOfTrades: noOfTrades === undefined || noOfTrades === null ? null : Number(noOfTrades),
    });
  }

  // Sort each bucket’s entries by yield desc (then ISIN) for stable display
  for (const y of Object.keys(index)) {
    index[y].sort((a, b) => {
      const dy = (b.yield ?? 0) - (a.yield ?? 0);
      if (dy !== 0) return dy;
      return String(a.isin || "").localeCompare(String(b.isin || ""));
    });
  }

  return index;
}

function buildYearToBenchmarkIndex(series) {
  const index = {};
  if (!Array.isArray(series)) return index;
  for (const item of series) {
    const year = Number(item?.year);
    if (!Number.isFinite(year)) continue;
    const countRaw =
      item?.verticalBenchmarkCount !== undefined && item?.verticalBenchmarkCount !== null
        ? Number(item.verticalBenchmarkCount)
        : item?.benchmarkCount !== undefined && item?.benchmarkCount !== null
          ? Number(item.benchmarkCount)
          : item?.count !== undefined && item?.count !== null
            ? Number(item.count)
            : null;
    index[year] = {
      avgYield:
        item?.verticalAvgYield !== undefined && item?.verticalAvgYield !== null
          ? Number(item.verticalAvgYield)
          : null,
      horizontalAvgYield:
        item?.horizontalAvgYield !== undefined && item?.horizontalAvgYield !== null
          ? Number(item.horizontalAvgYield)
          : null,
      avgMaturity:
        item?.verticalAvgMaturity !== undefined && item?.verticalAvgMaturity !== null
          ? Number(item.verticalAvgMaturity)
          : null,
      avgTradeValue:
        item?.verticalAvgTradeValue !== undefined && item?.verticalAvgTradeValue !== null
          ? Number(item.verticalAvgTradeValue)
          : null,
      avgScan:
        item?.verticalAvgScan !== undefined && item?.verticalAvgScan !== null
          ? Number(item.verticalAvgScan)
          : null,
      nssYield:
        item?.verticalNssYield !== undefined && item?.verticalNssYield !== null
          ? Number(item.verticalNssYield)
          : null,
      nssScan:
        item?.verticalNssScan !== undefined && item?.verticalNssScan !== null
          ? Number(item.verticalNssScan)
          : null,
      horizontalAvgScan:
        item?.horizontalAvgScan !== undefined && item?.horizontalAvgScan !== null
          ? Number(item.horizontalAvgScan)
          : null,
      totalTradeValue:
        item?.totalTradeValue !== undefined && item?.totalTradeValue !== null
          ? Number(item.totalTradeValue)
          : null,
      tenorTradeValue:
        item?.tenorTradeValue !== undefined && item?.tenorTradeValue !== null
          ? Number(item.tenorTradeValue)
          : null,
      count: Number.isFinite(countRaw) ? countRaw : null,
      isValid:
        item?.isValid === undefined || item?.isValid === null ? null : Boolean(item.isValid),
    };
  }
  return index;
}

function RenderBenchmarkEntryCell({ entry }) {
  if (!entry) {
    return (
      <Text type="secondary" style={{ fontSize: 11 }}>
        -
      </Text>
    );
  }
  const vertical = entry?.vertical || null;
  const horizontal = entry?.horizontal || null;
  const vYield = Number(vertical?.avgYield);
  const vScan = Number(vertical?.avgScan);
  const hYield = Number(horizontal?.avgYield);
  const hScan = Number(horizontal?.avgScan);
  const avgMaturity = Number(vertical?.avgMaturity ?? horizontal?.avgMaturity);
  const avgTradeValue = Number(vertical?.avgTradeValue ?? horizontal?.avgTradeValue);
  const totalTradeValue = Number(vertical?.totalTradeValue ?? horizontal?.totalTradeValue);
  const tenorTradeValue = Number(vertical?.tenorTradeValue ?? horizontal?.tenorTradeValue);
  const count = Number(vertical?.count ?? horizontal?.count);
  const cellIsValid = vertical?.isValid ?? horizontal?.isValid;

  let backgroundColor;
  let borderColor;
  // Zero trades → red (overrides invalid/orange)
  if (Number.isFinite(count) && count === 0) {
    backgroundColor = "rgba(248, 113, 113, 0.18)";
    borderColor = "rgba(248, 113, 113, 0.7)";
  } else if (cellIsValid === false) {
    backgroundColor = "rgba(234, 255, 6, 0.27)";
    borderColor = "rgba(227, 217, 12, 0.75)";
  } else if (Number.isFinite(count) && count > 0) {
    backgroundColor = "rgba(34, 197, 94, 0.15)";
    borderColor = "rgba(34, 197, 94, 0.7)";
  }

  const scanLabel = (v) => (Number.isFinite(v) ? v.toFixed(1) : "-");

  return (
    <div
      style={{
        fontSize: 10,
        lineHeight: 1.1,
        display: "flex",
        flexDirection: "column",
        gap: 2,
        padding: 3,
        borderRadius: 5,
        backgroundColor,
        border: backgroundColor ? `1px solid ${borderColor}` : undefined,
      }}
    >
    
      <div style={{ display: "flex", gap: 2, flexWrap: "wrap" }}>
      <ArrowDownOutlined />  <Tag color="blue" style={{ marginInlineEnd: 0, fontWeight: 700, fontSize: 10, paddingInline: 3, lineHeight: "14px", height: 16 }}>
          {Number.isFinite(vYield) ? vYield.toFixed(2) : "-"}
        </Tag>
        <Tag color="cyan" style={{ marginInlineEnd: 0, fontWeight: 700, fontSize: 10, paddingInline: 3, lineHeight: "14px", height: 16 }}>
          {scanLabel(vScan)}
        </Tag>
      </div>
      <div style={{ display: "flex", gap: 2, flexWrap: "wrap" }}>
       <ArrowRightOutlined/> <Tag color="blue" style={{ marginInlineEnd: 0, fontWeight: 700, fontSize: 10, paddingInline: 3, lineHeight: "14px", height: 16 }}>
          {Number.isFinite(hYield) ? hYield.toFixed(2) : "-"}
        </Tag>
        <Tag color="cyan" style={{ marginInlineEnd: 0, fontWeight: 700, fontSize: 10, paddingInline: 3, lineHeight: "14px", height: 16 }}>
          {scanLabel(hScan)}
        </Tag>
      </div>
   
      <span style={{ opacity: 0.8, fontSize: 10 }}>
        Total TV {Number.isFinite(totalTradeValue) ? `₹${totalTradeValue.toFixed(2)} Cr` : "-"}
      </span>
      <span style={{ opacity: 0.8, fontSize: 10 }}>
        Tenor TV {Number.isFinite(tenorTradeValue) ? `₹${tenorTradeValue.toFixed(2)} Cr` : "-"}
      </span>
      <span style={{ opacity: 0.8, fontSize: 10 }}>No. of Trades {Number.isFinite(count) ? count.toLocaleString() : "-"}</span>
    </div>
  );
}

function AvailabilityBands({ bands }) {
  const { top, left, width, height } = useDrawingArea();
  const xScale = useXScale();
  const theme = useTheme();
  const derivedFill = alpha(theme.palette.error.main, 0.14);
  const observedFill = alpha(theme.palette.success.main, 0.14);

  if (!Array.isArray(bands) || bands.length === 0) return null;

  return (
    <g>
      {bands.map((b, idx) => {
        const xStartRaw = xScale(b.startYear);
        const xEndRaw = xScale(b.endYear);
        if (xStartRaw === undefined || xEndRaw === undefined) return null;

        // Expand to cover the full year bucket width around center points.
        const xStart = xStartRaw - 10;
        const xEnd = xEndRaw + 10;
        const clippedStart = Math.max(xStart, left);
        const clippedEnd = Math.min(xEnd, left + width);
        if (clippedEnd <= clippedStart) return null;

        return (
          <rect
            key={`${b.type}-${b.startYear}-${b.endYear}-${idx}`}
            x={clippedStart}
            y={top}
            width={clippedEnd - clippedStart}
            height={height}
            fill={b.type === "observed" ? observedFill : derivedFill}
          />
        );
      })}
    </g>
  );
}

const RiskBasedPricing = () => {
  const { isDark } = useContext(ThemeContext);
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [benchmarkLoading, setBenchmarkLoading] = useState(false);
  const [error, setError] = useState(null);
  const [benchmarkError, setBenchmarkError] = useState(null);
  const [payload, setPayload] = useState(null);
  const [benchmarkPayload, setBenchmarkPayload] = useState(null);
  const [activeTab, setActiveTab] = useState("risk");
  const [cellIndex, setCellIndex] = useState({});
  const [modalState, setModalState] = useState({
    open: false,
    rating: "",
    tenor: null,
    yearBucket: null,
    entries: [],
  });
  const [benchmarkGraphState, setBenchmarkGraphState] = useState({
    open: false,
    rating: "",
    metric: "yield", // "yield" | "scan"
  });
  /** Full-viewport chart for benchmark Yield/SCAN graph modal. */
  const [benchmarkGraphMaximized, setBenchmarkGraphMaximized] = useState(false);

  const [viewportSize, setViewportSize] = useState(() =>
    typeof window !== "undefined" ? { w: window.innerWidth, h: window.innerHeight } : { w: 1200, h: 800 }
  );
  useEffect(() => {
    const onResize = () => setViewportSize({ w: window.innerWidth, h: window.innerHeight });
    window.addEventListener("resize", onResize);
    return () => window.removeEventListener("resize", onResize);
  }, []);

  const baseUrl = process.env.REACT_APP_BASE_URL || "http://localhost:8080/api/v1/sb";
  const apiUrl = `${baseUrl}/yield/risk-base-data`;
  const benchmarkApiUrl = `${baseUrl}/yield/benchmark-snapshot`;

  const fetchAllData = async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await apiClient.get(apiUrl);
      const body = response.data;
      if (body?.statusCode === 200 && body?.data) {
        setPayload(body.data);
      } else {
        throw new Error(body?.message || "Invalid response from yield API");
      }
      setCellIndex({});
    } catch (err) {
      console.error("Error fetching risk-base-data:", err);
      setError(err?.message || "Failed to fetch yield data");
      setPayload(null);
    } finally {
      setLoading(false);
    }
  };

  const fetchBenchmarkData = async () => {
    try {
      setBenchmarkLoading(true);
      setBenchmarkError(null);
      const response = await apiClient.get(benchmarkApiUrl);
      const body = response.data;
      if (body?.statusCode === 200 && body?.data) {
        setBenchmarkPayload(body.data);
      } else {
        throw new Error(body?.message || "Invalid response from benchmark API");
      }
    } catch (err) {
      console.error("Error fetching benchmark data:", err);
      setBenchmarkError(err?.message || "Failed to fetch benchmark data");
      setBenchmarkPayload(null);
    } finally {
      setBenchmarkLoading(false);
    }
  };

  useEffect(() => {
    fetchAllData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (activeTab === "benchmark" && !benchmarkPayload && !benchmarkLoading) {
      fetchBenchmarkData();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeTab, benchmarkPayload, benchmarkLoading]);

  const ratingKeys = useMemo(() => {
    const fromRisk = Object.keys(payload?.isinData || {});
    const fromBench = Object.keys(benchmarkPayload?.benchmarkData || {});
    const keys = [...new Set([...fromRisk, ...fromBench])];
    const ordered = DEFAULT_RATINGS_ORDER.filter((k) => keys.includes(k));
    const extras = keys.filter((k) => !DEFAULT_RATINGS_ORDER.includes(k)).sort();
    return [...ordered, ...extras];
  }, [payload, benchmarkPayload]);

  const entriesIndexByRating = useMemo(() => {
    const out = {};
    for (const rating of ratingKeys) {
      out[rating] = buildYearToEntriesIndex(payload?.isinData?.[rating]);
    }
    return out;
  }, [payload, ratingKeys]);

  const dataSource = useMemo(() => {
    return Array.from({ length: 15 }, (_, i) => {
      const tenor = i + 1;
      const row = { key: tenor, tenor };
      for (const rating of ratingKeys) {
        row[rating] = entriesIndexByRating?.[rating]?.[tenor] ?? [];
      }
      return row;
    });
  }, [ratingKeys, entriesIndexByRating]);

  const columns = useMemo(() => {
    const ratingColumns = ratingKeys.map((rating) => ({
      title: rating,
      dataIndex: rating,
      key: rating,
      width: 110,
      align: "left",
      render: (entries, record) => {
        if (!Array.isArray(entries) || entries.length === 0) {
          return (
            <Text type="secondary" style={{ fontSize: 11 }}>
              -
            </Text>
          );
        }

        const tenor = record?.tenor;
        const key = `${rating}__${tenor}`;
        const idx = cellIndex?.[key] ?? 0;
        const safeIdx = entries.length > 0 ? ((idx % entries.length) + entries.length) % entries.length : 0;
        const e = entries[safeIdx] || {};

        const yieldVal = Number(e?.yield);
        const scanVal = Number(e?.scanScore);

        const displayYield = Number.isFinite(yieldVal) ? `${yieldVal.toFixed(2)}%` : "-";
        const displayMonths =
          e?.residualMaturityMonths === null || e?.residualMaturityMonths === undefined || Number.isNaN(Number(e?.residualMaturityMonths))
            ? "-"
            : Number(e.residualMaturityMonths).toFixed(1);
        const displayScan = Number.isFinite(scanVal) ? scanVal.toFixed(2) : "-";
        const displayIsin = e?.isin || "";
        const showScan = rating !== "G-Sec" && rating !== "SDL";

        const openModal = () => {
          setModalState({
            open: true,
            rating,
            tenor,
            yearBucket: tenor,
            entries,
          });
        };

        const cellSummary = `Yield ${displayYield}${
          showScan ? ` • SCAN ${displayScan}` : ""
        }${
          displayIsin ? ` • ${displayIsin}` : ""
        }${displayMonths !== "-" ? ` • M ${displayMonths}m` : ""}`;

        return (
          <Tooltip title={cellSummary} placement="topLeft">
            <div
              className="yield-cell-clickable"
              role="button"
              tabIndex={0}
              onClick={openModal}
              onKeyDown={(evt) => {
                if (evt.key === "Enter" || evt.key === " ") openModal();
              }}
              style={{
                fontSize: 11,
                lineHeight: 1.15,
                cursor: "pointer",
                userSelect: "none",
              }}
            >
              <div className="yield-cell-clickable-text" style={{ display: "flex", flexDirection: "column", gap: 4 }}>
                <span
                  style={{
                    display: "inline-block",
                    fontWeight: 700,
                    color: Number.isFinite(yieldVal) ? yieldColor(yieldVal) : undefined,
                    background: Number.isFinite(yieldVal)
                      ? `${yieldColor(yieldVal)}1a`
                      : isDark
                        ? "rgba(255,255,255,0.06)"
                        : "rgba(0,0,0,0.04)",
                    border: `1px solid ${
                      Number.isFinite(yieldVal)
                        ? `${yieldColor(yieldVal)}40`
                        : isDark
                          ? "rgba(255,255,255,0.12)"
                          : "rgba(0,0,0,0.08)"
                    }`,
                    padding: "2px 6px",
                    borderRadius: 999,
                    width: "fit-content",
                  }}
                >
                  {displayYield}
                </span>
                {showScan ? (
                  <span
                    style={{
                      fontWeight: 600,
                      color: Number.isFinite(scanVal) ? scanColor(scanVal) : undefined,
                    }}
                  >
                    SCAN {displayScan}
                  </span>
                ) : null}
              </div>
              <div className="yield-cell-clickable-text" style={{ opacity: 0.6, marginTop: 2 }}>
                {Math.max(0, entries.length - 1)} more
              </div>
            </div>
          </Tooltip>
        );
      },
    }));

    return [
      {
        title: "Bucket",
        dataIndex: "tenor",
        key: "tenor",
        width: 90,
        fixed: "left",
        render: (value) => (
          <Text strong style={{ fontSize: 11 }}>
            {value}
          </Text>
        ),
      },
      ...ratingColumns,
    ];
  }, [ratingKeys, cellIndex]);

  const benchmarkEntriesIndexByRating = useMemo(() => {
    const out = {};
    const benchmarkData = benchmarkPayload?.benchmarkData || {};
    for (const rating of ratingKeys) {
      out[rating] = buildYearToBenchmarkIndex(benchmarkData?.[rating]);
    }
    return out;
  }, [benchmarkPayload, ratingKeys]);

  /** Taller plot in full-screen mode so series use the viewport. */
  const benchmarkModalChartHeight = useMemo(() => {
    if (!benchmarkGraphMaximized) return 360;
    return Math.max(400, viewportSize.h - 220);
  }, [benchmarkGraphMaximized, viewportSize.h]);

  const combinedBenchmarkDataSource = useMemo(() => {
    return Array.from({ length: 15 }, (_, i) => {
      const tenor = i + 1;
      const row = { key: tenor, tenor };
      for (const rating of ratingKeys) {
        const verticalEntry = benchmarkEntriesIndexByRating?.[rating]?.[tenor] ?? null;
        row[rating] = {
          vertical: verticalEntry,
          horizontal: verticalEntry
            ? {
                avgYield: verticalEntry.horizontalAvgYield,
                avgScan: verticalEntry.horizontalAvgScan,
                avgMaturity: verticalEntry.avgMaturity,
                avgTradeValue: verticalEntry.avgTradeValue,
                totalTradeValue: verticalEntry.totalTradeValue,
                tenorTradeValue: verticalEntry.tenorTradeValue,
                count: verticalEntry.count,
                isValid: verticalEntry.isValid,
              }
            : null,
        };
      }
      return row;
    });
  }, [ratingKeys, benchmarkEntriesIndexByRating]);

  const benchmarkColumns = useMemo(() => {
    const openGraphForRating = (rating, metric) => {
      setBenchmarkGraphMaximized(false);
      setBenchmarkGraphState({ open: true, rating, metric });
    };

    const sovereignBenchmarkRatings = new Set(["G-Sec", "SDL"]);
    const ratingColumns = ratingKeys.map((rating) => ({
      title: (
        <div className="rbp-benchmark-col-head">
          <span className="rbp-benchmark-col-head__rating">{rating}</span>
          <Tooltip title="Benchmark yield curves (vertical averages & NSS)">
            <Button
              type="primary"
              size="small"
              icon={<LineChartOutlined />}
              className="rbp-benchmark-graph-btn rbp-benchmark-graph-btn--yield"
              onClick={() => openGraphForRating(rating, "yield")}
            >
              Yield
            </Button>
          </Tooltip>
          {!sovereignBenchmarkRatings.has(rating) ? (
            <Tooltip title="Benchmark SCAN curves (vertical averages & NSS)">
              <Button
                type="default"
                size="small"
                icon={<RadarChartOutlined />}
                className="rbp-benchmark-graph-btn rbp-benchmark-graph-btn--scan"
                onClick={() => openGraphForRating(rating, "scan")}
              >
                SCAN
              </Button>
            </Tooltip>
          ) : null}
        </div>
      ),
      dataIndex: rating,
      key: rating,
      width: 100,
      align: "left",
      render: (entry) => <RenderBenchmarkEntryCell entry={entry} />,
      onCell: (record) => {
        const tenor = Number(record?.tenor);
        if (!Number.isFinite(tenor)) return {};

        const uptoAaRatings = new Set(["AAA", "AA+", "AA"]);
        const fromAaMinusRatings = new Set(["AA-", "A+", "A", "A-", "BBB+", "BBB"]);

        let disabled = false;
        if (sovereignBenchmarkRatings.has(rating)) {
          disabled = tenor >10;
        } else if (uptoAaRatings.has(rating)) {
          disabled = tenor > 10;
        } else if (fromAaMinusRatings.has(rating)) {
          disabled = tenor > 3;
        } else {
          // Any category outside these rating buckets remains disabled.
          disabled = true;
        }

        if (!disabled) return {};
        return {
          className: "benchmark-disabled-cell",
          style: {
            background: "rgba(89, 91, 94, 0.48)",
            boxShadow: "inset 0 0 0 9999px rgba(17,24,39,0.32)",
            pointerEvents: "none",
            filter: "grayscale(0.35)",
          },
        };
      },
    }));

    return [
      {
        title: "Bucket",
        dataIndex: "tenor",
        key: "tenor",
        width: 90,
        fixed: "left",
        render: (value) => (
          <Text strong style={{ fontSize: 11 }}>
            {value}
          </Text>
        ),
      },
      ...ratingColumns,
    ];
  }, [ratingKeys]);

  return (
    <LandingLayout>
      <div className={`rbp-page${isDark ? " rbp-page--dark" : ""}`}>
        

        <Tabs activeKey={activeTab} onChange={setActiveTab} items={[
          {
            key: "risk",
            label: "Daily Yields",
            children: (
              <>
                
                {loading ? (
                  <Card className="rbp-loading-card">
                    <div style={{ textAlign: "center", padding: "48px 0" }}>
                      <Spin size="large" />
                      <div style={{ marginTop: 12 }}>
                        <Text type="secondary">Loading yield data…</Text>
                      </div>
                    </div>
                  </Card>
                ) : error ? (
                  <Alert
                    message="Failed to load yield data"
                    description={error}
                    type="error"
                    showIcon
                    action={
                      <Button onClick={fetchAllData} icon={<ReloadOutlined />}>
                        Retry
                      </Button>
                    }
                  />
                ) : (
                  <Card className="rbp-table-card" bodyStyle={{ padding: 0 }}>
                    <Table
                      columns={columns}
                      dataSource={dataSource}
                      pagination={false}
                      size="small"
                      tableLayout="fixed"
                      className="yield-all-data-table"
                      rowClassName={(_, index) => (index % 2 === 0 ? "row-even" : "row-odd")}
                      scroll={RBP_TABLE_SCROLL}
                      sticky
                    />
                  </Card>
                )}
              </>
            ),
          },
          {
            key: "benchmark",
            label: "Benchmark",
            children: benchmarkLoading ? (
              <Card className="rbp-loading-card">
                <div style={{ textAlign: "center", padding: "48px 0" }}>
                  <Spin size="large" />
                  <div style={{ marginTop: 12 }}>
                    <Text type="secondary">Loading benchmark data…</Text>
                  </div>
                </div>
              </Card>
            ) : benchmarkError ? (
              <Alert
                message="Failed to load benchmark data"
                description={benchmarkError}
                type="error"
                showIcon
                action={
                  <Button onClick={fetchBenchmarkData} icon={<ReloadOutlined />}>
                    Retry
                  </Button>
                }
              />
            ) : (
              <Card className="rbp-table-card" bodyStyle={{ padding: 0 }}>
                <Table
                  columns={benchmarkColumns}
                  dataSource={combinedBenchmarkDataSource}
                  pagination={false}
                  size="small"
                  tableLayout="fixed"
                  className="yield-all-data-table"
                  rowClassName={(_, index) => (index % 2 === 0 ? "row-even" : "row-odd")}
                  scroll={RBP_TABLE_SCROLL}
                  sticky
                />
              </Card>
            ),
          },
        ]}
          type="card"
          size="large"
          className="risk-category-tabs"
        />
      </div>

      <Modal
        open={modalState.open}
        onCancel={() => setModalState((s) => ({ ...s, open: false }))}
        footer={null}
        width={900}
        rootClassName={`yield-all-data-modal${isDark ? " yield-all-data-modal--dark" : ""}`}
        title={
          <div style={{ display: "flex", flexDirection: "column", gap: 2 }}>
            <Text strong>
              {modalState.rating} • Bucket {modalState.tenor}
            </Text>
            <Text type="secondary" style={{ fontSize: 12 }}>
              Click a row to select it (it will be shown in the main table cell). Selected row is highlighted.
            </Text>
          </div>
        }
      >
        {(() => {
          const entries = Array.isArray(modalState.entries) ? modalState.entries : [];
          const tenor = modalState.tenor;
          const rating = modalState.rating;
          const selectedKey = `${rating}__${tenor}`;
          const selectedIdx = cellIndex?.[selectedKey] ?? 0;
          const safeSelectedIdx =
            entries.length > 0 ? ((selectedIdx % entries.length) + entries.length) % entries.length : 0;

          const modalRows = entries.map((e, idx) => ({
            key: `${e?.isin || "na"}-${idx}`,
            idx,
            isin: e?.isin || "",
            yield: e?.yield,
            residualMaturityMonths: e?.residualMaturityMonths,
            scanScore: e?.scanScore,
            tradeValue: e?.tradeValue,
            noOfTrades: e?.noOfTrades,
          }));

          const showScanInModal = rating !== "G-Sec" && rating !== "SDL";

          const modalColumns = [
            {
              title: "ISIN",
              dataIndex: "isin",
              key: "isin",
              width: 190,
              render: (v) => {
                const isin = String(v || "").trim();
                if (!isin) return "-";
                return (
                  <Typography.Link
                    onClick={(e) => {
                      e.preventDefault();
                      e.stopPropagation(); // don't change selected row when navigating
                      navigate(`/isin-details/${encodeURIComponent(isin)}`);
                    }}
                    style={{
                      fontFamily: "ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, monospace",
                      fontSize: 12,
                    }}
                  >
                    {isin}
                  </Typography.Link>
                );
              },
            },
            {
              title: "Yield",
              dataIndex: "yield",
              key: "yield",
              width: 90,
              align: "right",
              render: (v) => {
                const n = Number(v);
                if (!Number.isFinite(n)) return "-";
                const c = yieldColor(n);
                return <span style={{ color: c, fontWeight: 700 }}>{n.toFixed(2)}%</span>;
              },
            },
            {
              title: "Maturity (m)",
              dataIndex: "residualMaturityMonths",
              key: "residualMaturityMonths",
              width: 110,
              align: "right",
              render: (v) => (Number.isFinite(Number(v)) ? Number(v).toFixed(1) : "-"),
            },
          ];

          if (showScanInModal) {
            modalColumns.push({
              title: "SCAN",
              dataIndex: "scanScore",
              key: "scanScore",
              width: 90,
              align: "right",
              render: (v) => {
                const n = Number(v);
                if (!Number.isFinite(n)) return "-";
                const c = scanColor(n);
                return <span style={{ color: c, fontWeight: 700 }}>{n.toFixed(2)}</span>;
              },
            });
          }

          modalColumns.push(
            {
              title: "Trade Value",
              dataIndex: "tradeValue",
              key: "tradeValue",
              width: 120,
              align: "right",
              render: (v) => {
                const n = Number(v);
                if (!Number.isFinite(n)) return "-";
                // Value is already in crores (Cr) from backend
                return <span style={{ fontWeight: 600 }}>₹{n.toFixed(2)} Cr</span>;
              },
            },
            {
              title: "No. of Trades",
              dataIndex: "noOfTrades",
              key: "noOfTrades",
              width: 110,
              align: "right",
              render: (v) => {
                const n = Number(v);
                if (!Number.isFinite(n)) return "-";
                return <span style={{ fontWeight: 600 }}>{n.toLocaleString()}</span>;
              },
            }
          );

          return (
            <Table
              columns={modalColumns}
              dataSource={modalRows}
              pagination={false}
              size="small"
              tableLayout="fixed"
              rowClassName={(row, index) => {
                const zebra = index % 2 === 0 ? "row-even" : "row-odd";
                const selected = row.idx === safeSelectedIdx ? " selected-row" : "";
                return `${zebra}${selected}`;
              }}
              onRow={(row) => ({
                onClick: () => {
                  setCellIndex((prev) => ({ ...prev, [selectedKey]: row.idx }));
                  // keep modal open; selection highlight updates immediately
                },
              })}
              scroll={{ y: 420 }}
            />
          );
        })()}
      </Modal>

      <Modal
        open={benchmarkGraphState.open}
        onCancel={() => {
          setBenchmarkGraphMaximized(false);
          setBenchmarkGraphState({ open: false, rating: "", metric: "yield" });
        }}
        rootClassName={`yield-all-data-modal${isDark ? " yield-all-data-modal--dark" : ""}`}
        footer={null}
        width={benchmarkGraphMaximized ? "100vw" : 900}
        centered={!benchmarkGraphMaximized}
        style={
          benchmarkGraphMaximized
            ? { top: 0, paddingBottom: 0, margin: 0, maxWidth: "100vw" }
            : undefined
        }
        styles={
          benchmarkGraphMaximized
            ? {
                body: {
                  maxHeight: "calc(100vh - 72px)",
                  overflow: "auto",
                  padding: "12px 16px 20px",
                },
              }
            : undefined
        }
        title={
          <div
            style={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "flex-start",
              gap: 12,
              width: "100%",
              paddingRight: 4,
            }}
          >
            <div style={{ flex: 1, minWidth: 0 }}>
              <Text strong>
                {benchmarkGraphState.metric === "scan" ? "Benchmark SCAN Curves" : "Benchmark Yield Curves"} •{" "}
                {benchmarkGraphState.rating}
              </Text>
              <div>
                <Text type="secondary" style={{ fontSize: 12 }}>
                  Vertical averages (dark) and weighted Svensson NSS (purple) from the backend snapshot.
                </Text>
              </div>
            </div>
            <Button
              type="text"
              size="small"
              icon={benchmarkGraphMaximized ? <FullscreenExitOutlined /> : <FullscreenOutlined />}
              onClick={() => setBenchmarkGraphMaximized((v) => !v)}
              aria-label={benchmarkGraphMaximized ? "Exit full screen chart" : "Full screen chart"}
            />
          </div>
        }
      >
        {(() => {
          const rating = benchmarkGraphState.rating;
          const metric = benchmarkGraphState.metric;
          if (!rating || !benchmarkEntriesIndexByRating[rating]) {
            return (
              <Text type="secondary" style={{ fontSize: 12 }}>
                No benchmark data available for this rating.
              </Text>
            );
          }

          const verticalSeries = benchmarkEntriesIndexByRating[rating];
          const sovereignBenchmarkRatings = new Set(["G-Sec", "SDL"]);
          const uptoAaRatings = new Set(["AAA", "AA+", "AA"]);
          const fromAaMinusRatings = new Set(["AA-", "A+", "A", "A-", "BBB+", "BBB"]);
          const tenorLimit = sovereignBenchmarkRatings.has(rating)
            ? 10
            : uptoAaRatings.has(rating)
              ? 10
              : fromAaMinusRatings.has(rating)
                ? 3
                : 0;
          if (tenorLimit <= 0) {
            return (
              <Text type="secondary" style={{ fontSize: 12 }}>
                Graph not enabled for this rating.
              </Text>
            );
          }
          const years = Array.from({ length: tenorLimit }, (_, i) => i + 1);
          const counts = years.map((y) => {
            const v = Number(verticalSeries?.[y]?.count);
            return Number.isFinite(v) ? v : null;
          });
          const verticalData = years.map((y) => {
            const v = Number(verticalSeries?.[y]?.[metric === "scan" ? "avgScan" : "avgYield"]);
            return Number.isFinite(v) ? v : null;
          });
          const nssField = metric === "scan" ? "nssScan" : "nssYield";
          const nssData = years.map((y) => {
            const raw = verticalSeries?.[y]?.[nssField];
            const v = Number(raw);
            return Number.isFinite(v) ? v : null;
          });
          const hasNss = nssData.some((v) => Number.isFinite(v));
          const allValues = [
            ...verticalData,
            ...(hasNss ? nssData.filter((v) => Number.isFinite(v)) : []),
          ].filter((v) => Number.isFinite(v));

          if (allValues.length === 0) {
            return (
              <Text type="secondary" style={{ fontSize: 12 }}>
                No numeric {metric === "scan" ? "avg_scan" : "yield"} values available to plot for this rating.
              </Text>
            );
          }

          const minY = Math.min(...allValues);
          const maxY = Math.max(...allValues);
          const yPadding = Math.max(0.2, (maxY - minY) * 0.2);
          const yMin = minY - yPadding;
          const yMax = maxY + yPadding;

          // Build contiguous year bands:
          // observed => count > 0 (green), derived => count === 0 or missing (red).
          const availabilityBands = [];
          let startYear = years[0];
          let currentType = Number(counts[0]) > 0 ? "observed" : "derived";
          for (let i = 1; i < years.length; i += 1) {
            const yr = years[i];
            const t = Number(counts[i]) > 0 ? "observed" : "derived";
            if (t !== currentType) {
              availabilityBands.push({ startYear, endYear: years[i - 1], type: currentType });
              startYear = yr;
              currentType = t;
            }
          }
          availabilityBands.push({ startYear, endYear: years[years.length - 1], type: currentType });

          const verticalInvalid = years.map((y) => verticalSeries[y]?.isValid === false);

          return (
            <div>
              <BenchmarkGraphInvalidContext.Provider value={{ verticalInvalid }}>
              <LineChart
                slots={{ mark: BenchmarkLineChartMark }}
                xAxis={[
                  {
                    data: years,
                    scaleType: "point",
                    label: "Year",
                  },
                ]}
                yAxis={[
                  {
                    min: yMin,
                    max: yMax,
                    label: metric === "scan" ? "Avg Scan" : "Yield (%)",
                  },
                ]}
                series={[
                  {
                    id: "vertical",
                    data: verticalData,
                    label: metric === "scan" ? "Vertical avg scan" : "Vertical avg yield",
                    color: "#1f2937",
                    curve: "natural",
                    showMark: true,
                  },
                  ...(hasNss
                    ? [
                        {
                          id: "nss",
                          data: nssData,
                          label:
                            metric === "scan"
                              ? "Vertical NSS scan (weighted Svensson)"
                              : "Vertical NSS yield (weighted Svensson)",
                          color: "#7c3aed",
                          curve: "natural",
                          showMark: true,
                        },
                      ]
                    : []),
                ]}
                height={benchmarkModalChartHeight}
                margin={
                  benchmarkGraphMaximized
                    ? { top: 16, right: 20, bottom: 48, left: 56 }
                    : { top: 24, right: 24, bottom: 40, left: 56 }
                }
                slotProps={{
                  legend: {
                    direction: "row",
                    position: { vertical: "top", horizontal: "middle" },
                    itemMarkWidth: 14,
                    itemMarkHeight: 2,
                  },
                }}
              >
                <AvailabilityBands bands={availabilityBands} />
              </LineChart>
              </BenchmarkGraphInvalidContext.Provider>
              {!hasNss && (
                <Text type="secondary" style={{ fontSize: 12, display: "block", marginTop: 8 }}>
                  No NSS line in the legend: the snapshot has no weighted Svensson curve for this rating (backend needs enough
                  traded tenor points to fit).
                </Text>
              )}
              <div style={{ marginTop: 6, display: "flex", flexWrap: "wrap", gap: 14, fontSize: 12, alignItems: "center" }}>
                <span>
                  <span style={{ display: "inline-block", width: 10, height: 10, background: "rgba(34,197,94,0.35)", marginRight: 6 }} />
                  Observed section (`count` &gt; 0)
                </span>
                <span>
                  <span style={{ display: "inline-block", width: 10, height: 10, background: "rgba(239,68,68,0.35)", marginRight: 6 }} />
                  Derived section (`count` = 0 / missing)
                </span>
                <span style={{ display: "inline-flex", alignItems: "center", gap: 6 }}>
                  <svg width={18} height={18} viewBox="0 0 18 18" aria-hidden>
                    <circle cx={9} cy={9} r={6} fill="#fecaca" stroke="#dc2626" strokeWidth={1.2} />
                    <line x1={5.5} y1={5.5} x2={12.5} y2={12.5} stroke="#dc2626" strokeWidth={1.2} strokeLinecap="round" />
                    <line x1={5.5} y1={12.5} x2={12.5} y2={5.5} stroke="#dc2626" strokeWidth={1.2} strokeLinecap="round" />
                  </svg>
                  <span>
                    <code>isValid: false</code> (circle with × on vertical points)
                  </span>
                </span>
              </div>
            </div>
          );
        })()}
      </Modal>
    </LandingLayout>
  );
};

export default RiskBasedPricing;
